import {
  Box,
  Input as ChakraInput,
  InputGroup,
  InputLeftAddon,
  InputLeftElement,
  InputProps as ChakraInputProps,
  InputRightAddon,
  InputRightElement,
} from '@chakra-ui/react'
import React from 'react'

import { IconKey } from '~shared-components/icons/brand/IconKey'
import { IconMail } from '~shared-components/icons/brand/IconMail'
import { IconPhone } from '~shared-components/icons/brand/IconPhone'
import { IconSearch } from '~shared-components/icons/brand/IconSearch'
import { IconSquareDelete } from '~shared-components/icons/brand/IconSquareDelete'
import { IconUser } from '~shared-components/icons/brand/IconUser'

import { useSharedForwardedRef } from '../../hooks/useSharedForwardedRef'
import { Currency } from '../../transforms'
import { Helper } from '../Helper/Helper'

export interface InputProps extends ChakraInputProps {
  'aria-label'?: string
  'aria-labelledby'?: string
  currency?: Currency
  'data-testid'?: string
  error?: boolean
  helper?: React.ReactNode | string
  leftAddon?: React.ReactNode | string
  leftInsert?: React.ReactNode | string
  onEnter?: (value: string) => void
  onValueChange?: (value: string) => void
  rightAddon?: React.ReactNode | string
  rightInsert?: React.ReactNode | string
  showClearButton?: boolean
  type?: 'text' | 'password' | 'email' | 'tel' | 'user' | 'currency' | 'search'
  unit?: React.ReactNode | string
  value?: string | undefined
}

const iconProps = { boxSize: '20px', color: 'neutral.30' }
export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (props, ref) => {
    const {
      currency,
      error,
      helper,
      leftAddon,
      leftInsert,
      onChange,
      onEnter,
      onValueChange,
      pattern,
      rightAddon,
      rightInsert,
      showClearButton,
      size = 'md',
      type = 'text',
      unit,
      value,
      width = '100%',
      ...rest
    } = props

    const sharedRef = useSharedForwardedRef(ref)

    const onClear = () => {
      // Clear uncontrolled input
      if (sharedRef?.current && value == undefined) sharedRef.current.value = ''
      // and call onVChange (for both controlled and uncontrolled)
      onValueChange?.('')
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault()
      const value = e.target.value

      if (
        value &&
        pattern &&
        type === 'text' &&
        !new RegExp(pattern).test(value)
      ) {
        return // input not allowed
      }

      onChange?.(e)
      onValueChange?.(value)
    }

    let fixedBugHeight = '40px'
    switch (size) {
      case 'xs':
        fixedBugHeight = '24px'
        break
      case 'sm':
        fixedBugHeight = '32px'
        break
      case 'lg':
        fixedBugHeight = '48px'
        break
      case 'md':
      default:
        fixedBugHeight = '40px'
    }

    const leftAddonComponent = leftAddon

    const leftElement =
      leftInsert ||
      (type === 'currency' && currency ? (
        currency
      ) : type === 'tel' ? (
        <IconPhone {...iconProps} />
      ) : type === 'email' ? (
        <IconMail {...iconProps} />
      ) : type === 'user' ? (
        <IconUser {...iconProps} />
      ) : type === 'password' ? (
        <IconKey {...iconProps} />
      ) : null)

    const rightAddonComponent = rightAddon || unit

    const rightElement =
      rightInsert ||
      (showClearButton && value?.length ? (
        <IconSquareDelete cursor="pointer" onClick={onClear} {...iconProps} />
      ) : type === 'search' ? (
        <IconSearch {...iconProps} />
      ) : null)

    const inputType =
      type === 'currency' || type === 'search' || type === 'user'
        ? 'text'
        : type

    return (
      <Box width={width}>
        <InputGroup alignItems="stretch" width={width}>
          {leftElement && (
            <InputLeftElement height={fixedBugHeight} zIndex={2}>
              {leftElement}
            </InputLeftElement>
          )}
          {!leftElement && leftAddonComponent && (
            <InputLeftAddon>{leftAddonComponent}</InputLeftAddon>
          )}
          <ChakraInput
            ref={sharedRef}
            height={fixedBugHeight}
            isInvalid={error}
            onChange={handleChange}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                value && onEnter?.(value)
                e.preventDefault()
              }
            }}
            size={size}
            type={inputType}
            value={value}
            variant="outline"
            zIndex="1"
            {...rest}
          />
          {rightElement && (
            <InputRightElement height={fixedBugHeight} zIndex={2}>
              {rightElement}
            </InputRightElement>
          )}
          {!rightElement && rightAddonComponent && (
            <InputRightAddon>{rightAddonComponent}</InputRightAddon>
          )}
        </InputGroup>
        {helper && <Helper {...props}>{helper}</Helper>}
      </Box>
    )
  },
)

Input.displayName = 'Input'
